import type { Home, User } from '@lmc/ts-common'
import { mixpanel } from 'common/services/analytics'
import type { Dict } from 'mixpanel-browser'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

export enum MixpanelEvents {
  PAGE_VISITED = 'Page visited',
  ROSTERING_REPORT_DOWNLOADED = 'Rostering_Report_Downloaded',
  ROTA_BASED_ACCESS_CHANGED = 'Rota_Based_Access_Changed',
  FAMILY_ROSTERING_SETTINGS_CHANGED = 'Family_Rostering_Settings_Changed',
  ROTA_BUILDER_TAB_VIEWED = 'Rota_Builder_Tab_Viewed',
  START_ROTA_GENERATED = 'Start_Rota_Generated',
  ROTA_FILTERS_APPLIED = 'Rota_Filters_Applied',
  ROTA_PATTERN_EDIT_UPDATE_EVENTS = 'Rota_Pattern_Edit_Update_Events',
  ACCESS_CONTROL_HELP_ARTICLE_CLICKED = 'Access_Control_Help_Article_Clicked',
  ROTA_EVENTS_VIEW_TOGGLED = 'Rota_Events_View_Toggled',
  EMAR_V2_STOCK_RECORD_CREATED = 'EMAR_V2_Stock_Record_Created',
  EMAR_V2_DISCREPANCY_CLOSED = 'EMAR_V2_Discrepancy_Closed',
  EMAR_V2_DISPOSAL_MODAL_OPENED_FROM_DISCONTINUE = 'EMAR_V2_Disposal_Modal_Opened_From_Discontinue',
  EMAR_V2_DISPOSE_LATER_CLICKED_FROM_DISCONTINUE = 'EMAR_V2_Dispose_Later_Clicked_From_Discontinue',
  EMAR_V2_HOMEPAGE_CARD_LINK_CLICKED = 'EMAR_V2_Homepage_Card_Link_Clicked',
  WARNING_NOTIFICATIONS_SET = 'Warning_Notifications_Set',
  RESIDENT_STATUS_UPDATED = 'Resident_Status_Updated',
  REASONABLE_ADJUSTMENT_ADDED = 'Reasonable_Adjustment_Added',
  REASONABLE_ADJUSTMENT_EDITED = 'Reasonable_Adjustment_Edited',
  REASONABLE_ADJUSTMENT_DELETED = 'Reasonable_Adjustment_Deleted',
  DOCUMENT_UPLOADED = 'Document_Uploaded',
  PAY_RATE_ADDED = 'Pay_Rate_Added',
  PAY_RATE_EDITED = 'Pay_Rate_Edited',
  PAY_RATE_ASSIGNMENT_ADDED = 'Pay_Rate_Assignment_Added',
  PAY_RATE_ASSIGNMENT_EDITED = 'Pay_Rate_Assignment_Edited',
  RECONCILIATION_BULK_ACTION = 'Reconciliation_Bulk_Action',
  RECONCILIATION_CUSTOM_PAY_AMOUNT = 'Reconciliation_Custom_Pay_Amount',
  RECONCILIATION_CLOCK_IN_OUT_EDITED = 'Reconciliation_Clock_In_Out_Edited',
  RECONCILIATION_DONT_PAY = 'Reconciliation_Dont_Pay',
  PAY_RATE_OVERRIDE_ADDED = 'Pay_Rate_Override_Added',
  PAY_REPORT_CSV_DOWNLOADED = 'Pay_Report_CSV_Downloaded',
  PAY_REPORT_BREAKDOWN_CSV_DOWNLOADED = 'Pay_Report_Breakdown_CSV_Downloaded',
  CHARGE_REPORT_CSV_DOWNLOADED = 'Charge_Report_CSV_Downloaded',
  CHARGE_REPORT_BREAKDOWN_CSV_DOWNLOADED = 'Charge_Report_Breakdown_CSV_Downloaded',
  EMAR_V2_BACK_SIGN_USED = 'EMAR_V2_Back_Sign_Used',
  INITIAL_ASSESSMENT_COMPLETED = 'initial_assessment_completed',
  RISK_ASSESSMENT_COMPLETED = 'risk_assessment_completed',
  SERVICE_USER_CREATED = 'service_user_created',
  INVOICING_TOGGLED = 'Invoicing_Toggled',
  CHARGE_RATE_ADDED = 'Charge_Rate_Added',
  CHARGE_RECONCILIATION_BULK_ACTION = 'Charge_Reconciliation_Bulk_Action',
  CHARGE_RECONCILIATION_CUSTOM_AMOUNT = 'Charge_Reconciliation_Custom_Amount',
  CHARGE_RECONCILIATION_DONT_CHARGE = 'Charge_Reconciliation_Dont_Charge',
  CHARGE_REPORT_OPENED = 'Charge_Report_Opened',
  BODY_MAP_RESOLVED_REPORT_CLICK_BLOCKED = 'Body_Map_Resolved_Report_Click_Blocked',
  INCIDENT_CASE_EXITED = 'Incident case exited',
  INCIDENT_CASE_STEP_VIEWED = 'Incident case step viewed'
}

// homeGroup can arrive as an id or a populated object; only ever send the id
const resolveId = (value: unknown): string | undefined => {
  if (value && typeof value === 'object') {
    const { _id, id } = value as { _id?: unknown; id?: unknown }
    const resolved = _id ?? id
    return resolved != null ? String(resolved) : undefined
  }
  return value != null ? String(value) : undefined
}

export const sendMixpanelEvent = (
  event: MixpanelEvents,
  props: Dict,
  home: Home,
  user: User
) => {
  const homeGroupId = resolveId(home?.homeGroup) ?? 'No home group'
  const homeId = resolveId(home?.id) ?? 'Unknown Home'

  mixpanel.track(event, {
    group: homeGroupId,
    homeGroup: homeGroupId,
    homeName: home?.name || 'Unknown Home',
    home: homeId,
    userId: user?.id || 'Unknown User',
    ...props
  })
}

const useMixpanelEvent = () => {
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const sendEvent = useCallback(
    (event: MixpanelEvents, props: Dict) => {
      sendMixpanelEvent(event, props, home, user)
    },
    [home, user]
  )

  return sendEvent
}

export default useMixpanelEvent
